import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Info } from 'lucide-react';
import styles from './InfoTip.module.css';

interface InfoTipProps {
  children: React.ReactNode;
  label?: string;
  size?: 'xs' | 'sm';
  placement?: 'top' | 'bottom';
}

/**
 * Small clickable info icon with a claymorphism-styled popover.
 * Toggles on click (works on touch devices); also opens on hover for desktop pointers.
 * Closes on outside click and Escape.
 */
export function InfoTip({ children, label = 'More info', size = 'xs', placement = 'top' }: InfoTipProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span ref={wrapRef} className={styles['wrap']} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        className={`${styles['trigger']} ${styles[`trigger-${size}`]}`}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <Info aria-hidden />
      </button>

      <AnimatePresence>
        {open && (
          <motion.span
            role="tooltip"
            className={`${styles['popover']} ${styles[`popover-${placement}`]}`}
            initial={{ opacity: 0, y: placement === 'top' ? 4 : -4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: placement === 'top' ? 4 : -4, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 500, damping: 32 }}
          >
            {children}
            <span className={`${styles['arrow']} ${styles[`arrow-${placement}`]}`} aria-hidden />
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}
